import html2canvas from "html2canvas";
import {
  applyBackgroundStyles,
  restoreBackgroundStyles,
  snapshotBackgroundStyles,
  getHtml2CanvasBackgroundColor,
} from "../ProjectV2/utils/projectBackground";

const RENDER_SCALE = 2; // render 2× for sharpness, then downscale back

/**
 * Downscales (or resizes) a canvas to destWidth × destHeight using OffscreenCanvas.
 */
async function resizeCanvasToBlob(sourceCanvas, destWidth, destHeight) {
  const offscreen = new OffscreenCanvas(destWidth, destHeight);
  const ctx = offscreen.getContext("2d");

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(sourceCanvas, 0, 0, destWidth, destHeight);
  return offscreen.convertToBlob({ type: "image/png" });
}

function isBulletFlexLayout(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.matches(".bullet") || el.classList.contains("bullet")) return true;
  if (el.parentElement?.querySelector(":scope > .bullet")) return true;
  const wrap = el.closest(".flex-wrap");
  return Boolean(wrap && wrap.querySelector(".bullet"));
}

function isLeafTextContainer(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.closest("[data-html2canvas-ignore]")) return false;
  if (isBulletFlexLayout(el)) return false;
  if (el.matches("img, svg, canvas, video, br, script, style")) return false;
  if (el.querySelector("img, svg, canvas, video, p, div, h1, h2, h3, h4, li, table, tr, td")) {
    return false;
  }
  const text = el.textContent;
  return Boolean(text && text.trim());
}

/**
 * Read the live (browser) line breaks of an element by walking text-node
 * glyphs and watching getClientRects() Y changes. This is the preview wrap.
 */
export function getWrappedTextLines(el) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  const lines = [];
  let current = "";
  let lastTop = null;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const value = node.nodeValue;
    if (!value) continue;

    for (let i = 0; i < value.length; i += 1) {
      range.setStart(node, i);
      range.setEnd(node, i + 1);
      const rect = range.getClientRects()[0];
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        current += value[i];
        continue;
      }

      if (
        lastTop !== null &&
        Math.abs(rect.top - lastTop) > Math.max(2, rect.height * 0.35)
      ) {
        lines.push(current);
        current = value[i];
      } else {
        current += value[i];
      }
      lastTop = rect.top;
    }
  }

  if (current !== "" || lines.length === 0) lines.push(current);

  let tail = el.lastChild;
  while (tail && tail.nodeType === 3 && !String(tail.nodeValue || "").trim()) {
    tail = tail.previousSibling;
  }
  if (tail && tail.nodeName === "BR") lines.push("");

  return lines;
}

function applyFrozenLines(el, lines) {
  const fragment = document.createDocumentFragment();
  lines.forEach((line, i) => {
    if (i > 0) fragment.appendChild(document.createElement("br"));
    const span = document.createElement("span");
    span.setAttribute("data-frozen-line", "true");
    span.style.whiteSpace = "nowrap";
    span.textContent = line;
    fragment.appendChild(span);
  });
  el.replaceChildren(fragment);
}

/**
 * Snapshot live wrapping on every leaf text node, then replace it with
 * explicit nowrap lines + <br>. html2canvas re-lays out text with its own
 * glyph metrics; without this, a word like "Mark" can jump up a line.
 */
export function freezePreviewWrapping(root) {
  if (!root) return () => {};

  // Only freeze wrapping <p> credit lines (song title/details, blurbs).
  // Name-only paragraph bullets live in a flex-wrap + CSS grid; rewriting
  // those nodes stacks every name on top of each other in html2canvas.
  const candidates = [...root.querySelectorAll("p")].filter(isLeafTextContainer);

  const snapshots = candidates.map((el) => {
    const lines = getWrappedTextLines(el);
    return {
      el,
      html: el.innerHTML,
      whiteSpace: el.style.whiteSpace,
      width: el.style.width,
      maxWidth: el.style.maxWidth,
      boxSizing: el.style.boxSizing,
      flexShrink: el.style.flexShrink,
      lines,
    };
  });

  snapshots.forEach((snap) => {
    const liveWidth = snap.el.getBoundingClientRect().width;
    if (liveWidth > 0) {
      snap.el.style.boxSizing = "border-box";
      snap.el.style.width = `${liveWidth}px`;
      snap.el.style.maxWidth = `${liveWidth}px`;
      snap.el.style.flexShrink = "0";
    }
    applyFrozenLines(snap.el, snap.lines);
  });

  return () => {
    snapshots.forEach((snap) => {
      snap.el.innerHTML = snap.html;
      snap.el.style.whiteSpace = snap.whiteSpace;
      snap.el.style.width = snap.width;
      snap.el.style.maxWidth = snap.maxWidth;
      snap.el.style.boxSizing = snap.boxSizing;
      snap.el.style.flexShrink = snap.flexShrink;
    });
  };
}

function getCaptureSize(element) {
  const root = element.getBoundingClientRect();
  let width = Math.max(element.offsetWidth, element.scrollWidth, 1);
  let height = Math.max(element.offsetHeight, element.scrollHeight, 1);

  element.querySelectorAll("*").forEach((child) => {
    const box = child.getBoundingClientRect();
    if (!box.width && !box.height) return;
    width = Math.max(width, box.right - root.left);
    height = Math.max(height, box.bottom - root.top);
  });

  return {
    width: Math.max(1, Math.ceil(width)),
    height: Math.max(1, Math.ceil(height)),
  };
}

/**
 * Captures a single DOM element as a sharp PNG Blob.
 * Pass targetWidth to scale the PNG to the export resolution without
 * reflowing the live preview (so wrapping stays identical).
 */
export async function captureElementAsPng(element, options = {}) {
  const {
    scale = RENDER_SCALE,
    backgroundColor,
    backgroundStyle = null,
    targetWidth = null,
  } = options;

  const bgSnapshot = backgroundStyle
    ? snapshotBackgroundStyles(element)
    : null;
  if (backgroundStyle) {
    applyBackgroundStyles(element, backgroundStyle);
  }

  const canvasBackgroundColor =
    backgroundColor !== undefined
      ? backgroundColor
      : backgroundStyle
        ? getHtml2CanvasBackgroundColor(backgroundStyle)
        : null;

  await document.fonts.ready;
  const unfreeze = freezePreviewWrapping(element);

  try {
    const { width: captureWidth, height: captureHeight } =
      getCaptureSize(element);

    const canvas = await html2canvas(element, {
      backgroundColor: canvasBackgroundColor,
      scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      // Native layout, not per-glyph spans — per-letter rendering changes wrap.
      letterRendering: false,
      width: captureWidth,
      height: captureHeight,
      scrollX: 0,
      scrollY: 0,
    });

    const destWidth = targetWidth
      ? Math.round(targetWidth)
      : Math.round(canvas.width / scale);
    const destHeight = Math.round(
      canvas.height * (destWidth / canvas.width),
    );
    const blob = await resizeCanvasToBlob(canvas, destWidth, destHeight);

    return {
      blob,
      width: destWidth,
      height: destHeight,
      cssWidth: destWidth,
      cssHeight: destHeight,
    };
  } finally {
    unfreeze();
    if (bgSnapshot) {
      restoreBackgroundStyles(element, bgSnapshot);
    }
  }
}

/**
 * Captures every .blockWrapper element and returns ordered PNG blobs.
 */
export async function generateBlockImages(options = {}) {
  const {
    scale = RENDER_SCALE,
    onProgress,
    backgroundStyle = null,
    getBackgroundStyleForBlock = null,
    targetWidth = null,
    backgroundColor,
  } = options;

  const blocks = document.querySelectorAll(".blockWrapper");
  if (!blocks.length) throw new Error("No .blockWrapper elements found");

  // The preview may be zoomed (fullscreen fit). Zoom must be OFF while
  // capturing: html2canvas and the rect-based measurements here assume
  // unzoomed layout pixels. Resetting zoom never reflows the credits —
  // it only changes display size — so the capture itself is unaffected.
  const zoomWrapper = document.getElementById("preview-zoom-wrapper");
  const previousZoom = zoomWrapper ? zoomWrapper.style.zoom : null;
  if (zoomWrapper) zoomWrapper.style.zoom = 1;

  try {
    const results = [];
    let offsetY = 0;

    for (let idx = 0; idx < blocks.length; idx++) {
      const item = blocks[idx];
      const blockBackgroundStyle = getBackgroundStyleForBlock
        ? getBackgroundStyleForBlock(item, idx, offsetY)
        : backgroundStyle;

      const { blob, width, height, cssWidth, cssHeight } =
        await captureElementAsPng(item, {
          scale,
          backgroundStyle: blockBackgroundStyle,
          targetWidth,
          backgroundColor,
        });

      results.push({ blob, idx, width, height, cssWidth, cssHeight });
      onProgress?.(idx + 1, blocks.length);
      offsetY += item.offsetHeight;
    }

    return results;
  } finally {
    if (zoomWrapper) zoomWrapper.style.zoom = previousZoom ?? "";
  }
}

/**
 * Stitches multiple PNG blobs into a single vertical strip.
 */
export async function generateStitchedImage(blockImages) {
  const bitmaps = await Promise.all(
    blockImages.map(({ blob }) => createImageBitmap(blob)),
  );

  const totalWidth = Math.max(...bitmaps.map((b) => b.width));
  const totalHeight = bitmaps.reduce((sum, b) => sum + b.height, 0);

  const offscreen = new OffscreenCanvas(totalWidth, totalHeight);
  const ctx = offscreen.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, totalWidth, totalHeight);

  let y = 0;
  for (const bitmap of bitmaps) {
    ctx.drawImage(bitmap, 0, y);
    y += bitmap.height;
    bitmap.close();
  }

  return offscreen.convertToBlob({ type: "image/png" });
}

export async function generateScrollPng(options = {}) {
  const blockImages = await generateBlockImages(options);
  return generateStitchedImage(blockImages);
}
